import React from "react";
import { RouterProvider } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { AccessTokenProvider } from "./hooks/useAuthentication";
import { AccessProvider } from "./hooks/useAccess";
import { UnsavedChangesProvider } from "./lib/unsavedChanges";
import { SplitViewProvider } from "./lib/splitView";
import { router } from "./router";
import "./index.css";

// ERRORS ONLY. Performance tracing at 100% sent one envelope POST to Sentry per
// request the app made, and on a browser that blocks sentry.io (ad-blocker /
// privacy setting) each one is a red `net::ERR_BLOCKED_BY_CLIENT` line -- the
// "ton of POST errors" on Committed Orders, 2026-11-02, none of them our API.
// Uncaught errors and captureException still report.
Sentry.init({
  dsn: "https://238d6cdf2970e0cb14c17243eb728cbb@o1168654.ingest.us.sentry.io/4508252116484096",
  tracesSampleRate: 0,
});

// App is just the provider stack + the router. All top-level navigation lives
// in `router.tsx` (createHashRouter — hash history keeps URLs as `#/…`, so the
// S3/CloudFront static hosting needs no rewrite rules) and is described by the
// manifest in `nav.ts`. The app shell (Header, MSAL auth gate, Suspense,
// unsaved-changes blocker) lives in `components/RootLayout.tsx`.
//
// Provider order matters: UnsavedChangesProvider + AccessTokenProvider must sit
// ABOVE RouterProvider so RootLayout (and the NavBlocker's useBlocker) can read
// their context from inside the router tree. AccessProvider (who may use what,
// from GET /me) sits under AccessTokenProvider because it waits for the bearer.
// SplitViewProvider is above the router for the same reason as the other two:
// RootLayout reads it to lay out the panes, and the pane's OWN router tree
// reads it to report where it navigated to.
const App: React.FC = () => (
  <UnsavedChangesProvider>
    <AccessTokenProvider>
      <AccessProvider>
        <SplitViewProvider>
          <RouterProvider router={router} />
        </SplitViewProvider>
      </AccessProvider>
    </AccessTokenProvider>
  </UnsavedChangesProvider>
);

export default App;
